import React from 'react';
import {useLocation, useNavigationType} from 'react-router-dom';

/**
 * OGNI PAGINA NUOVA SI APRE DALL'ALTO. Il sito cambia pagina senza
 * ricaricare (react-router), e il browser lascia la finestra dov'era: chi
 * apriva una categoria, Promozioni o una pagina di Aiuto dal fondo di una
 * pagina lunga restava in fondo anche alla nuova, e se la nuova era corta
 * finiva sul piede. Qui si torna in cima quando:
 *   - si apre un indirizzo nuovo (voce nuova della cronologia, PUSH): un
 *     altro percorso o un'altra scheda/categoria (?scheda=…);
 *   - si clicca il collegamento della pagina in cui si e' gia' (Casino' dal
 *     fondo del Casino'): react-router non cambia pagina, ma chi clicca si
 *     aspetta di ripartire dall'alto.
 * Non si torna in cima:
 *   - con «indietro» e «avanti» (POP): li' le lobby riportano dove si era
 *     (useRicordaScorrimento in Casino/Pagine.js) e il browser fa il resto;
 *   - quando la pagina riscrive il suo indirizzo (REPLACE): filtri,
 *     fornitore, «Mostra di più», la finestra di accesso che si chiude;
 *   - con le ancore (#…), che portano gia' al loro punto.
 * Di colpo, senza animazione: e' una pagina nuova, non una corsa verso l'alto.
 */
export default function useCimaPagina() {
  const {pathname, search, hash} = useLocation();
  const come = useNavigationType();
  const prima = React.useRef(pathname + search);

  React.useLayoutEffect(() => {
    const qui = pathname + search;
    const cambiata = qui !== prima.current;
    prima.current = qui;
    if (!cambiata || come !== 'PUSH' || hash) return;
    window.scrollTo(0, 0);
  }, [pathname, search, hash, come]);

  /* Il collegamento della pagina aperta: react-router lo tratta come «resta
     qui», quindi l'indirizzo non cambia e l'effetto sopra non parte. Si
     ascolta in cattura, prima che il collegamento faccia il suo lavoro, e si
     confronta con l'indirizzo che il sito ha aperto. */
  const aperta = React.useRef(pathname + search);
  aperta.current = pathname + search;
  React.useEffect(() => {
    const clic = (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || (a.target && a.target !== '_self')) return;
      const dove = new URL(a.getAttribute('href'), window.location.href);
      if (dove.origin !== window.location.origin || dove.hash) return;
      if (dove.pathname + dove.search === aperta.current) window.scrollTo(0, 0);
    };
    document.addEventListener('click', clic, true);
    return () => document.removeEventListener('click', clic, true);
  }, []);
}
